import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { FileText, ShieldCheck, ArrowRight, X, Phone, Mail, MapPin, Clock, Filter, CheckCircle2, Upload } from "lucide-react";
import PhotoUpload from "../components/PhotoUpload";

const STAGES = [
  { id: "submitted",              label: "Submitted",              tint: "bg-sun" },
  { id: "documents_verified",     label: "Documents verified",     tint: "bg-brand-blue text-white" },
  { id: "submitted_to_authority", label: "Filed at authority",     tint: "bg-brand text-white" },
  { id: "certificate_received",   label: "Certificate received",   tint: "bg-leaf" },
  { id: "delivered",              label: "Delivered",              tint: "bg-brand-green text-white" },
];
const STAGE_LABEL = Object.fromEntries(STAGES.map(s => [s.id, s.label]));

export default function AdminPetRegistrations() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/pet-registrations`, { params: filter ? { status: filter } : {} });
      setItems(r.data.items || []);
      setStats(r.data.stats || {});
    } catch {
      // backend will 403 non-admins; the role guard below handles redirect
    }
  };
  useEffect(() => { if (user?.role === "super_admin") load(); /* eslint-disable-next-line */ }, [filter, user?.role]);

  if (loading) return null;
  if (!user || user.role !== "super_admin") return <Navigate to="/app" replace/>;

  return (
    <div className="space-y-6" data-testid="admin-petreg-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold inline-flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-brand"/> Pet registrations
          </h1>
          <p className="text-ink-soft text-sm">Concierge requests from pet parents.</p>
        </div>
      </header>

      {/* Stats tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="petreg-stats">
        <StatTile k="Awaiting pay" v={stats.awaiting_payment || 0} onClick={() => setFilter("awaiting_payment")} active={filter === "awaiting_payment"}/>
        {STAGES.map(s => (
          <StatTile key={s.id} k={s.label} v={stats[s.id] || 0} onClick={() => setFilter(s.id)} active={filter === s.id}/>
        ))}
      </div>

      {filter && (
        <button onClick={() => setFilter("")} data-testid="petreg-clear-filter" className="text-xs font-bold text-brand inline-flex items-center gap-1">
          <Filter className="w-3 h-3"/> Clear filter (showing: {STAGE_LABEL[filter] || filter})
        </button>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {items.length === 0 && <div className="text-ink-soft text-sm">No requests in this view yet.</div>}
        {items.map(r => (
          <button
            key={r.request_id}
            data-testid={`admin-petreg-card-${r.request_id}`}
            onClick={() => setSelected(r)}
            className="text-left bg-surface border border-line rounded-3xl p-5 shadow-soft hover:-translate-y-1 hover:shadow-hover transition-all"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-soft">{r.city} · {r.authority.split(" — ")[0]}</div>
                <h4 className="font-heading text-lg font-extrabold">{r.pet_name} <span className="text-ink-soft font-bold">· {r.species}</span></h4>
                <div className="text-xs text-ink-soft mt-1 inline-flex items-center gap-1.5"><MapPin className="w-3 h-3"/>{r.owner_address}</div>
              </div>
              <Badge stage={r.status}/>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <div><div className="text-ink-soft">Owner</div><div className="font-bold truncate">{r.user_name}</div></div>
              <div><div className="text-ink-soft">Phone</div><div className="font-bold">{r.owner_phone}</div></div>
              <div><div className="text-ink-soft">Total paid</div><div className="font-bold">{r.paid_at ? `₹${r.total_amount}` : "—"}</div></div>
            </div>
          </button>
        ))}
      </div>

      {selected && <Drawer r={selected} onClose={() => setSelected(null)} onUpdated={async () => { setSelected(null); await load(); }}/>}
    </div>
  );
}

function StatTile({ k, v, onClick, active }) {
  return (
    <button onClick={onClick} className={`text-left rounded-2xl p-3 border transition-all ${active ? "bg-brand text-white border-brand" : "bg-surface border-line hover:border-brand"}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{k}</div>
      <div className="font-heading text-2xl font-extrabold mt-0.5">{v}</div>
    </button>
  );
}

function Badge({ stage }) {
  const s = STAGES.find(x => x.id === stage);
  return <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${s?.tint || "bg-line text-ink-soft"}`}>{s?.label || stage}</span>;
}

function Drawer({ r, onClose, onUpdated }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose}>
      <aside onClick={e => e.stopPropagation()} className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-surface p-6 overflow-y-auto" data-testid="admin-petreg-drawer">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-ink-soft">{r.city} · {r.authority}</div>
            <h2 className="font-heading text-2xl font-extrabold">{r.pet_name}</h2>
            <div className="text-xs text-ink-soft">{r.species} · {r.breed || "—"} · {r.age_years} yrs · {r.gender} · {r.pet_color}</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5"/></button>
        </div>

        <div className="space-y-1.5 text-sm">
          <Row k="Owner" v={`${r.user_name} (${r.user_email})`}/>
          <Row k="Phone" v={<span className="inline-flex items-center gap-1"><Phone className="w-3 h-3"/>{r.owner_phone}</span>}/>
          <Row k="Address" v={r.owner_address}/>
          <Row k="Govt fee" v={`₹${r.govt_fee}`}/>
          <Row k="PetBond fee" v={`₹${r.service_fee}`}/>
          <Row k="Total" v={`₹${r.total_amount}`} bold/>
          <Row k="Paid at" v={r.paid_at ? new Date(r.paid_at).toLocaleString() : "—"}/>
          <Row k="SLA" v={`${r.sla_days} working days`}/>
        </div>

        <section className="mt-5">
          <h3 className="font-heading text-base font-extrabold mb-2">Documents</h3>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(r.documents || {}).map(([k, url]) => url ? (
              <a key={k} href={url} target="_blank" rel="noreferrer" className="bg-surface-muted rounded-xl p-2 text-xs font-bold hover:bg-brand/10" data-testid={`admin-petreg-doc-${k}`}>
                <FileText className="w-3.5 h-3.5 inline-block mr-1 text-brand"/>{k.replace(/_/g, " ")}
              </a>
            ) : null)}
          </div>
          {r.notes && <div className="mt-3 text-xs text-ink-soft italic">Parent notes: "{r.notes}"</div>}
        </section>

        <section className="mt-5">
          <h3 className="font-heading text-base font-extrabold mb-2">Timeline</h3>
          <ol className="space-y-2 text-xs">
            {(r.stage_history || []).map((h, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-leaf mt-0.5 shrink-0"/>
                <div>
                  <div className="font-bold">{STAGE_LABEL[h.stage] || h.stage}</div>
                  <div className="text-ink-soft">{new Date(h.at).toLocaleString()} · {h.by}{h.note ? ` — ${h.note}` : ""}</div>
                </div>
              </li>
            ))}
            {(r.stage_history || []).length === 0 && <div className="text-ink-soft">No history yet.</div>}
          </ol>
        </section>

        {r.status !== "awaiting_payment" && r.status !== "delivered" && (
          <>
            <DeliverForm r={r} onUpdated={onUpdated}/>
            <AdvanceForm r={r} onUpdated={onUpdated}/>
          </>
        )}
        {r.status === "awaiting_payment" && <div className="mt-5 bg-sun/40 border border-sun rounded-2xl p-3 text-sm font-bold">Parent has not paid yet. Once they pay you'll be able to advance stages here.</div>}
        {r.status === "delivered" && (
          <div className="mt-5 bg-leaf/30 border border-leaf rounded-2xl p-3 text-sm">
            <div className="font-bold">Delivered — certificate {r.certificate_number || ""} sent to parent.</div>
            {r.certificate_url && <a href={r.certificate_url} target="_blank" rel="noreferrer" className="underline text-brand font-bold inline-flex items-center gap-1 mt-1"><FileText className="w-3 h-3"/>View certificate</a>}
          </div>
        )}
      </aside>
    </div>
  );
}

function DeliverForm({ r, onUpdated }) {
  // Always-visible "Upload certificate & mark delivered" shortcut. The whole
  // point of the admin tool is this — make it impossible to miss.
  const [certUrl, setCertUrl] = useState("");
  const [certNum, setCertNum] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!certUrl) { setErr("Please upload the certificate file first."); return; }
    if (!certNum.trim()) { setErr("Certificate number is required."); return; }
    setSaving(true);
    try {
      // If skipping intermediate stages, the backend allows jumping straight
      // to 'delivered' (it enforces forward-only, not strict-next).
      await axios.patch(`${API}/admin/pet-registrations/${r.request_id}`, {
        stage: "delivered",
        note: note || `Certificate delivered (${certNum})`,
        certificate_url: certUrl,
        certificate_number: certNum,
      });
      onUpdated();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not deliver.");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="mt-5 bg-brand/5 border-2 border-brand/40 rounded-2xl p-4 space-y-3" data-testid="admin-petreg-deliver-form">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-brand text-white grid place-items-center"><Upload className="w-4 h-4"/></div>
        <div>
          <h3 className="font-heading text-base font-extrabold">Upload certificate &amp; mark delivered</h3>
          <p className="text-[11px] text-ink-soft">Final step — closes the request and notifies the parent.</p>
        </div>
      </div>
      <label className="block">
        <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Certificate number *</span>
        <input value={certNum} onChange={e => setCertNum(e.target.value)} className="inp" placeholder="e.g. BBMP/PL/2026/00128" data-testid="admin-petreg-deliver-cert-num"/>
      </label>
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-ink-soft mb-1.5 inline-flex items-center gap-1">
          <FileText className="w-3 h-3 text-brand"/> Certificate PDF / image *
        </div>
        <PhotoUpload value={certUrl} onChange={setCertUrl} label="" testid="admin-petreg-deliver-cert-upload" acceptPdf={true} maxMB={5} aspect="aspect-[3/2]"/>
        {certUrl && (
          <div className="text-[11px] text-leaf font-bold mt-1.5 inline-flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3"/> File ready to deliver
          </div>
        )}
      </div>
      <label className="block">
        <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Note to parent (optional)</span>
        <input value={note} onChange={e => setNote(e.target.value)} className="inp" placeholder="e.g. Physical copy will reach you in 3 days"/>
      </label>
      {err && <div className="text-sm font-bold text-danger" data-testid="admin-petreg-deliver-err">{err}</div>}
      <button disabled={saving || !certUrl || !certNum} data-testid="admin-petreg-deliver-btn" className="w-full bg-brand text-white rounded-full py-3 text-sm font-bold inline-flex items-center justify-center gap-2 hover:bg-brand-hover disabled:opacity-60">
        {saving ? "Delivering…" : <>Mark delivered &amp; notify parent <ArrowRight className="w-4 h-4"/></>}
      </button>
      <style>{`.inp{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.55rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.inp:focus{border-color:#FF6B35;}`}</style>
    </form>
  );
}

function AdvanceForm({ r, onUpdated }) {
  // Intermediate stage progression only — final delivery is handled by DeliverForm
  // above so the admin always has a clear "upload cert" surface.
  const order = STAGES.map(s => s.id);
  const idx = order.indexOf(r.status);
  const nextOptions = order.slice(idx + 1).filter(s => s !== "delivered");
  const [stage, setStage] = useState(nextOptions[0] || "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  if (nextOptions.length === 0) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/pet-registrations/${r.request_id}`, { stage, note });
      onUpdated();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not advance.");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="mt-5 bg-surface-muted rounded-2xl p-4 space-y-3" data-testid="admin-petreg-advance-form">
      <h3 className="font-heading text-base font-extrabold">Or advance an intermediate stage</h3>
      <label className="block">
        <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Move to</span>
        <select value={stage} onChange={e => setStage(e.target.value)} data-testid="admin-petreg-stage-select" className="inp">
          {nextOptions.map(s => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Internal note (optional)</span>
        <input value={note} onChange={e => setNote(e.target.value)} className="inp" placeholder="e.g. Filing receipt #12345"/>
      </label>
      {err && <div className="text-sm font-bold text-danger" data-testid="admin-petreg-err">{err}</div>}
      <button disabled={saving} data-testid="admin-petreg-advance-btn" className="bg-ink text-white rounded-full px-4 py-2.5 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60">
        {saving ? "Saving…" : <>Confirm {STAGE_LABEL[stage]} <ArrowRight className="w-4 h-4"/></>}
      </button>
      <style>{`.inp{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.55rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.inp:focus{border-color:#FF6B35;}`}</style>
    </form>
  );
}

function Row({ k, v, bold }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-ink-soft text-xs uppercase tracking-wider font-bold">{k}</span>
      <span className={`text-right ${bold ? "font-heading text-base font-extrabold" : "font-bold text-sm"}`}>{v}</span>
    </div>
  );
}
